import React from 'react';
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Td,
  Th,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatGroup,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../hooks/useAuth';
import config from '../config';

const PayoutDashboard = () => {
  const { user } = useAuth();

  const { data: statistics } = useQuery({
    queryKey: ['payout-statistics'],
    queryFn: async () => {
      const response = await axios.get(`${config.API_BASE_URL}/api/payouts/statistics`);
      return response.data.data;
    }
  });

  // Calculate summary statistics
  const totalPayout = statistics?.reduce((sum, payout) => sum + payout.totalAmount, 0) || 0;
  const totalLeads = statistics?.reduce((sum, payout) => sum + payout.totalLeads, 0) || 0;
  const approvedLeads = statistics?.reduce((sum, payout) => sum + payout.approvedLeads, 0) || 0;
  const conversionRate = totalLeads ? ((approvedLeads / totalLeads) * 100).toFixed(1) : 0;

  // Prepare chart data
  const chartData = statistics?.map((payout) => ({
    month: new Date(payout.month).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    }),
    amount: payout.totalAmount,
    leads: payout.approvedLeads,
  })) || [];

  return (
    <Container maxW="container.xl" py={8}>
      <Heading mb={6}>Payout Dashboard</Heading>

      <StatGroup mb={8}>
        <Stat>
          <StatLabel>Total Earnings</StatLabel>
          <StatNumber>₹{totalPayout.toLocaleString()}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Total Leads</StatLabel>
          <StatNumber>{totalLeads}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Approved Leads</StatLabel>
          <StatNumber>{approvedLeads}</StatNumber>
        </Stat>
        <Stat>
          <StatLabel>Conversion Rate</StatLabel>
          <StatNumber>{conversionRate}%</StatNumber>
        </Stat>
      </StatGroup>

      <Box mb={8} h="400px">
        <Heading size="md" mb={4}>
          Monthly Performance
        </Heading>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="amount"
              stroke="#3182CE"
              name="Earnings (₹)"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="leads"
              stroke="#48BB78"
              name="Approved Leads"
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Box overflowX="auto">
        <Table>
          <Thead>
            <Tr>
              <Th>Month</Th>
              <Th>Total Leads</Th>
              <Th>Approved Leads</Th>
              <Th>Commission Rate</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {statistics?.map((payout) => (
              <Tr key={payout._id}>
                <Td>
                  {new Date(payout.month).toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </Td>
                <Td>{payout.totalLeads}</Td>
                <Td>{payout.approvedLeads}</Td>
                <Td>{(payout.commissionRate * 100).toFixed(1)}%</Td>
                <Td>₹{payout.totalAmount.toLocaleString()}</Td>
                <Td>
                  <Badge
                    colorScheme={payout.status === 'paid' ? 'green' : 'yellow'}
                  >
                    {payout.status}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {user?.role === 'agent' && (
        <Text mt={4} color="gray.600" fontSize="sm">
          Note: Payouts are processed monthly based on approved leads and the
          current commission rate of 2%.
        </Text>
      )}
    </Container>
  );
};

export default PayoutDashboard;
